import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  LogOut, RefreshCw, CheckCircle2, XCircle, Loader2, Sparkles,
  MapPin, Mail, Phone, Globe, Award, TrendingUp, AlertCircle,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusFilters = [
  { key: "pending_review", label: "Pending" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
  { key: "all", label: "All" },
];

function bandColor(band) {
  switch ((band || "").toLowerCase()) {
    case "enterprise-ready": return "bg-emerald-100 text-emerald-800 border-emerald-200";
    case "ready": return "bg-brand-wine/10 text-brand-wine border-brand-wine/20";
    case "progressing": return "bg-amber-100 text-amber-800 border-amber-200";
    default: return "bg-brand-paper2 text-brand-muted border-brand-line";
  }
}

export default function AdminDashboard() {
  const { user, logout, authHeaders } = useAuth();
  const [tab, setTab] = useState("pending_review");
  const [data, setData] = useState({ applications: [], stats: {} });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [acting, setActing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/applications`, {
        headers: authHeaders(),
        params: { status_filter: tab },
      });
      setData(res.data);
    } catch (e) {
      toast.error("Failed to load applications");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const applications = data.applications || [];
  const stats = data.stats || {};

  const filtered = applications;

  useEffect(() => {
    if (selected) {
      const fresh = applications.find((a) => a.id === selected.id);
      if (fresh) setSelected(fresh);
    }
  }, [applications]); // eslint-disable-line

  const act = async (id, action) => {
    setActing(true);
    try {
      await axios.post(`${API}/admin/applications/${id}/${action}`, {}, { headers: authHeaders() });
      toast.success(`Application ${action}d`);
      await load();
    } catch (e) {
      toast.error(`Failed to ${action}`);
    } finally {
      setActing(false);
    }
  };

  const regenerate = async (id) => {
    setActing(true);
    try {
      await axios.post(`${API}/admin/applications/${id}/regenerate-ai`, {}, { headers: authHeaders() });
      toast.success("AI regeneration queued");
      setTimeout(load, 4000);
    } catch (e) {
      toast.error("Failed to regenerate");
    } finally {
      setActing(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-paper2" data-testid="admin-dashboard-page">
      <header className="bg-brand-cream border-b border-brand-line sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center">
              <img src="/yuukke-logo-light.png" alt="Yuukke" className="h-8 w-auto" />
            </Link>
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-brand-wine border-l border-brand-line pl-3">
              Admin
            </span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-brand-muted hidden md:inline" data-testid="admin-user-email">{user?.email}</span>
            <button
              onClick={load}
              className="flex items-center gap-2 text-sm text-brand-muted hover:text-brand-wine transition-colors"
              data-testid="admin-refresh-btn"
            >
              <RefreshCw size={14} /> Refresh
            </button>
            <button
              onClick={logout}
              className="flex items-center gap-2 text-sm text-brand-wine hover:text-brand-wineDark transition-colors"
              data-testid="admin-logout-btn"
            >
              <LogOut size={14} /> Log out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="admin-stats">
          <StatCard label="Total" value={stats.total ?? "—"} testid="stat-total" />
          <StatCard label="Pending" value={stats.pending ?? "—"} tone="amber" testid="stat-pending" />
          <StatCard label="Approved" value={stats.approved ?? "—"} tone="emerald" testid="stat-approved" />
          <StatCard label="Rejected" value={stats.rejected ?? "—"} tone="rose" testid="stat-rejected" />
        </div>

        <div className="flex items-center gap-2 mb-6" data-testid="admin-tabs">
          {statusFilters.map((f) => (
            <button
              key={f.key}
              onClick={() => { setTab(f.key); setSelected(null); }}
              className={`px-4 py-2 rounded-full text-sm font-medium border transition-all ${
                tab === f.key
                  ? "bg-brand-wine text-white border-brand-wine"
                  : "bg-white text-brand-ink border-brand-line hover:border-brand-wine/40"
              }`}
              data-testid={`admin-tab-${f.key}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="grid lg:grid-cols-5 gap-6">
          <div className="lg:col-span-2 space-y-3" data-testid="admin-app-list">
            {loading && <div className="text-brand-muted text-sm">Loading applications…</div>}
            {!loading && filtered.length === 0 && (
              <div className="text-brand-muted text-sm bg-white border border-brand-line rounded-xl p-6 text-center">
                No applications in this bucket.
              </div>
            )}
            {!loading && filtered.map((a) => {
              const score = a?.ai?.readiness_score;
              const active = selected?.id === a.id;
              return (
                <button
                  key={a.id}
                  onClick={() => setSelected(a)}
                  className={`w-full text-left bg-white rounded-xl border p-5 transition-all ${
                    active ? "border-brand-wine shadow-[0_10px_30px_-15px_rgba(139,30,63,0.35)]" : "border-brand-line hover:border-brand-wine/40"
                  }`}
                  data-testid={`admin-app-item-${a.id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h3 className="font-serif text-lg font-semibold text-brand-ink truncate">
                        {a.business_name}
                      </h3>
                      <p className="text-xs text-brand-muted mt-0.5 truncate">{a.full_name} · {a.category}</p>
                    </div>
                    {a.ai_status === "ready" && score !== undefined ? (
                      <ScoreBubble score={score} />
                    ) : a.ai_status === "pending" ? (
                      <span className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-1 flex items-center gap-1">
                        <Loader2 size={11} className="animate-spin" /> AI
                      </span>
                    ) : (
                      <span className="text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded-full px-2 py-1 flex items-center gap-1">
                        <AlertCircle size={11} /> AI failed
                      </span>
                    )}
                  </div>
                  <div className="mt-3 flex items-center gap-3 text-xs text-brand-muted">
                    <StatusPill status={a.status} />
                    <span>·</span>
                    <span className="truncate">{new Date(a.created_at).toLocaleString()}</span>
                  </div>
                </button>
              );
            })}
          </div>

          <div className="lg:col-span-3">
            {selected ? (
              <DetailPanel
                app={selected}
                onApprove={() => act(selected.id, "approve")}
                onReject={() => act(selected.id, "reject")}
                onRegenerate={() => regenerate(selected.id)}
                acting={acting}
              />
            ) : (
          <div className="bg-white border border-brand-line rounded-2xl p-12 text-center text-brand-muted">
                <Sparkles size={22} className="mx-auto text-brand-wine mb-3" />
                Select an application to view AI-generated profile and readiness assessment.
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

function StatCard({ label, value, tone, testid }) {
  const tones = {
    amber: "text-amber-800 bg-amber-50 border-amber-200",
    emerald: "text-emerald-800 bg-emerald-50 border-emerald-200",
    rose: "text-rose-800 bg-rose-50 border-rose-200",
  };
  return (
    <div className={`rounded-2xl border p-5 bg-white border-brand-line`} data-testid={testid}>
      <div className={`inline-block text-xs uppercase tracking-widest font-semibold rounded-full px-2 py-0.5 border ${tones[tone] || "text-brand-wine bg-brand-wine/10 border-brand-wine/20"}`}>
        {label}
      </div>
      <div className="mt-3 font-serif text-3xl font-medium text-brand-ink">{value}</div>
    </div>
  );
}

function ScoreBubble({ score }) {
  const s = Number(score) || 0;
  const color = s >= 85 ? "bg-emerald-600" : s >= 70 ? "bg-brand-wine" : s >= 50 ? "bg-amber-500" : "bg-rose-500";
  return (
    <div className={`w-11 h-11 rounded-full ${color} text-white flex items-center justify-center text-sm font-semibold shrink-0`} title="AI readiness score">
      {s}
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    pending_review: { text: "Pending", cls: "text-amber-800 bg-amber-50 border-amber-200" },
    approved: { text: "Approved", cls: "text-emerald-800 bg-emerald-50 border-emerald-200" },
    rejected: { text: "Rejected", cls: "text-rose-800 bg-rose-50 border-rose-200" },
  };
  const m = map[status] || { text: status, cls: "text-brand-muted bg-brand-paper2 border-brand-line" };
  return (
    <span className={`text-[11px] uppercase tracking-widest font-semibold rounded-full px-2 py-0.5 border ${m.cls}`}>
      {m.text}
    </span>
  );
}

function DetailPanel({ app, onApprove, onReject, onRegenerate, acting }) {
  const ai = app.ai || {};
  return (
    <div className="bg-white border border-brand-line rounded-2xl overflow-hidden" data-testid="admin-app-detail">
      <div className="p-6 md:p-8 border-b border-brand-line">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <StatusPill status={app.status} />
            <h2 className="mt-3 font-serif text-3xl font-medium text-brand-ink leading-tight">
              {app.business_name}
            </h2>
            {ai.tagline && (
              <p className="mt-2 text-brand-wine italic font-serif text-lg">&ldquo;{ai.tagline}&rdquo;</p>
            )}
          </div>
          {app.ai_status === "ready" && ai.readiness_score !== undefined && (
            <div className="flex items-center gap-3">
              <ScoreBubble score={ai.readiness_score} />
              <div>
                <p className="text-xs uppercase tracking-widest text-brand-muted">Readiness</p>
                <span className={`inline-block mt-0.5 text-xs font-semibold px-2 py-0.5 rounded-full border ${bandColor(ai.readiness_band)}`}>
                  {ai.readiness_band || "—"}
                </span>
              </div>
            </div>
          )}
        </div>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-brand-muted">
          <InfoRow icon={<Mail size={14} />} label={app.email} />
          {app.phone && <InfoRow icon={<Phone size={14} />} label={app.phone} />}
          {app.city && <InfoRow icon={<MapPin size={14} />} label={app.city} />}
          {app.website && <InfoRow icon={<Globe size={14} />} label={app.website} />}
        </div>
      </div>

      <div className="p-6 md:p-8 space-y-6">
        <div>
          <h3 className="text-xs uppercase tracking-[0.25em] font-semibold text-brand-wine mb-2">
            Owner's Description
          </h3>
          <p className="text-[15px] text-brand-ink leading-relaxed">{app.description}</p>
        </div>

        {app.ai_status === "ready" ? (
          <>
            <Section title="AI-Generated Elevator Pitch" icon={<Sparkles size={14} />}>
              <p className="text-[15px] text-brand-ink leading-relaxed">{ai.elevator_pitch}</p>
            </Section>

            <Section title="Standardized Capabilities" icon={<Award size={14} />}>
              <div className="flex flex-wrap gap-2">
                {(ai.capabilities || []).map((c) => (
                  <Badge key={c} className="bg-brand-paper2 text-brand-ink border border-brand-line rounded-full px-3 py-1 font-normal">
                    {c}
                  </Badge>
                ))}
              </div>
            </Section>

            <Section title="Buyer Search Keywords" icon={<TrendingUp size={14} />}>
              <div className="flex flex-wrap gap-2">
                {(ai.buyer_keywords || []).map((k) => (
                  <span key={k} className="text-xs text-brand-wine bg-brand-wine/5 border border-brand-wine/15 rounded-full px-3 py-1">
                    #{k}
                  </span>
                ))}
              </div>
            </Section>

            <div className="grid md:grid-cols-2 gap-6">
              <Section title="Strengths">
                <ul className="space-y-2 text-[14px] text-brand-ink">
                  {(ai.strengths || []).map((s, i) => (
                    <li key={i} className="flex gap-2"><span className="text-emerald-600">▲</span>{s}</li>
                  ))}
                </ul>
              </Section>
              <Section title="Procurement Gaps">
                <ul className="space-y-2 text-[14px] text-brand-ink">
                  {(ai.gaps || []).map((g, i) => (
                    <li key={i} className="flex gap-2"><span className="text-amber-600">△</span>{g}</li>
                  ))}
                </ul>
              </Section>
            </div>

            <Section title="Recommended Next Steps">
              <ol className="space-y-2 text-[14px] text-brand-ink list-decimal pl-5">
                {(ai.recommendations || []).map((r, i) => <li key={i}>{r}</li>)}
              </ol>
            </Section>
          </>
        ) : app.ai_status === "pending" ? (
          <div className="p-6 bg-amber-50 border border-amber-200 rounded-xl text-amber-800 flex items-center gap-3">
            <Loader2 size={18} className="animate-spin" />
            AI profile generation in progress — this usually takes 10–20 seconds.
          </div>
        ) : (
          <div className="p-6 bg-rose-50 border border-rose-200 rounded-xl text-rose-800">
            <p className="font-semibold flex items-center gap-2"><AlertCircle size={16} /> AI generation failed</p>
            {app.ai_error && <p className="mt-2 text-sm">{app.ai_error}</p>}
          </div>
        )}
      </div>

      <div className="p-6 md:p-8 border-t border-brand-line bg-brand-paper2/40 flex flex-wrap gap-3">
        <button
          disabled={acting || app.status === "approved"}
          onClick={onApprove}
          className="flex items-center gap-2 bg-brand-wine text-white rounded-lg px-6 py-3 font-medium hover:bg-brand-wineDark transition-colors disabled:opacity-50"
          data-testid="admin-approve-btn"
        >
          <CheckCircle2 size={16} /> Approve
        </button>
        <button
          disabled={acting || app.status === "rejected"}
          onClick={onReject}
          className="flex items-center gap-2 bg-white border border-brand-line text-rose-700 rounded-lg px-6 py-3 font-medium hover:bg-rose-50 transition-colors disabled:opacity-50"
          data-testid="admin-reject-btn"
        >
          <XCircle size={16} /> Reject
        </button>
        <button
          disabled={acting}
          onClick={onRegenerate}
          className="flex items-center gap-2 bg-white border border-brand-line text-brand-ink rounded-lg px-6 py-3 font-medium hover:border-brand-wine/40 transition-colors disabled:opacity-50 ml-auto"
          data-testid="admin-regenerate-btn"
        >
          <RefreshCw size={14} /> Regenerate AI
        </button>
      </div>
    </div>
  );
}

function InfoRow({ icon, label }) {
  return <div className="flex items-center gap-2">{icon}<span className="truncate">{label}</span></div>;
}

function Section({ title, icon, children }) {
  return (
    <div>
      <h3 className="text-xs uppercase tracking-[0.25em] font-semibold text-brand-wine mb-3 flex items-center gap-2">
        {icon}{title}
      </h3>
      {children}
    </div>
  );
}
